import { useContext, useState } from 'react';
import { DatePicker } from '@givebutter/primitives/date-picker';
import * as Popover from '@givebutter/primitives/popover';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ReportContext } from 'assets/js/pages/reports/ReportContext';
import { DateRange } from 'assets/js/pages/reports/types';
import { getDatetimeRangeFromUpstream } from 'assets/js/pages/reports/util';
import { RelativeDateOperatorOptions } from 'components/filter/RelativeDateOperatorOptions';
import { RelativeDateRangeOperator } from 'components/filter/types';
import { TableCoreStateContext } from 'components/table/TableCoreStateContext';
import { getReadableSingleResourceFromReportResource } from 'components/table/util';
import * as Tooltip from 'components/tooltip/Tooltip';
import { useCurrentReport } from 'customHooks/useCurrentReport';
import { DateTime } from 'luxon';

const ReportDateRange = () => {
    const { data: report } = useCurrentReport();
    const {
        dateRange: _dateRange,
        setDateRange,
        dateRangeType,
        setDateRangeType,
    } = useContext(ReportContext);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const { isPending, query } = useContext(TableCoreStateContext);
    const upstreamDateRange = query?.data.meta.date_range.range;
    const isCustom = dateRangeType === 'CUSTOM';
    const dateRange =
        !isCustom && upstreamDateRange?.every(r => !!r)
            ? getDatetimeRangeFromUpstream(upstreamDateRange as [string, string])
            : _dateRange;
    const [start, end] = dateRange;

    return (
        <div className={'flex items-center gap-2'}>
            <div
                className={'flex h-8 w-fit items-center rounded-lg border border-gray-300 bg-white'}
            >
                <SingleSelect.Root
                    value={dateRangeType}
                    onValueChange={(value: RelativeDateRangeOperator) => {
                        if (value) {
                            setDateRangeType(value);
                            if (value === 'CUSTOM') {
                                setIsDatePickerOpen(true);
                            }
                        }
                    }}
                >
                    <SingleSelect.Trigger
                        size="sm"
                        aria-label="Report date range"
                        className="basis-min-content rounded-br-none rounded-tr-none border-y border-r border-solid border-gray-300 font-medium ring-0"
                    />
                    <SingleSelect.Content onCloseAutoFocus={e => e.preventDefault()}>
                        <RelativeDateOperatorOptions />
                    </SingleSelect.Content>
                </SingleSelect.Root>
                <Popover.Root open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <Popover.Trigger
                        disabled={!isCustom && (isPending || query?.isRefetching)}
                        className={'p-3 text-sm font-medium disabled:opacity-60'}
                    >
                        {`${formatDateRange(start)} - ${formatDateRange(end)}`}
                    </Popover.Trigger>
                    <Popover.Portal>
                        <Popover.Content align={'start'} sideOffset={2}>
                            <DatePicker
                                isTextInput={false}
                                selectRange={true}
                                value={dateRange}
                                onChange={date => {
                                    setDateRange(date as DateRange);
                                    setDateRangeType('CUSTOM');
                                }}
                            />
                        </Popover.Content>
                    </Popover.Portal>
                </Popover.Root>
            </div>
            <Tooltip.Root>
                <Tooltip.Trigger>
                    <FontAwesomeIcon icon={faCircleInfo} />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content inverse>
                        <p>
                            Select a relative or custom date range to filter all data in the
                            visualization and table below. This filter also applies to aggregate
                            columns that sum up a{' '}
                            {getReadableSingleResourceFromReportResource(report.resource)}
                            ’s activity.
                        </p>
                        <Tooltip.Arrow inverse />
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip.Root>
        </div>
    );
};

const formatDateRange = (date: DateTime) => date.toLocaleString(DateTime.DATE_MED);

export { ReportDateRange };
